// Correcting an end time that falls before its start, and measuring a duration.
//
// A timelog can legitimately cross midnight: 22:00 to 02:00 is four hours. Both
// time-entry modals therefore push the end forward a day when it lands before
// the start. That correction used to fire on `isSameOrBefore`, which also caught
// an end EQUAL to its start and turned a zero-length log into a 24-hour one.
//
// That is how CapLegal entry 1241937 rendered as a 23-hour block: CapLegal holds
// a zero-duration entry, the CL sync stores it as 08:00 to 08:00, the next-day
// rule made it 24 hours, and the calendar's 23-hour ceiling clamped it. It
// inflated one employee's Monday total to 26:50.
//
// A zero-length event is zero, not a midnight crossing, so the test is
// `isBefore`. Kept in one place because two modals need the identical rule and
// had drifted into two copies of it.

/**
 * The end moment, pushed forward one day only if it truly precedes the start.
 * Returns a new moment when it corrects, and the original when it does not, so
 * callers can always use the return value.
 *
 * @param {import('moment').Moment} startMoment
 * @param {import('moment').Moment} endMoment
 * @returns {import('moment').Moment}
 */
export function nextDayCorrectedEnd(startMoment, endMoment) {
  if (endMoment.isBefore(startMoment)) {
    return endMoment.clone().add(1, 'day');
  }
  return endMoment;
}

/**
 * Duration in decimal hours between two moments, applying the midnight-crossing
 * correction. Zero when the two are equal.
 *
 * @param {import('moment').Moment} startMoment
 * @param {import('moment').Moment} endMoment
 * @returns {number}
 */
export function durationHours(startMoment, endMoment) {
  const correctedEnd = nextDayCorrectedEnd(startMoment, endMoment);
  return correctedEnd.diff(startMoment, 'minutes') / 60;
}
